import { FC, ReactNode } from 'react';
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Palette,
  Text,
} from '@updater/ui-uds';
import {
  PlanCardList,
  PlanCardListItemInstantOn,
  PlanCardListItemTelevision,
  PlanCardListItemBuildings,
  PlanCardListItemDiscount,
  PlanCardDummyOfferDisclaimer,
  PlanCardListItemContractLength,
  PlanCardListItemAttOffer,
  PlanCardListItemAutoPayDisclaimer,
  PlanCardListItemStarryOffer,
  PlanCardListItemStarryEquipmentDetail,
  PlanCardListItemStarlinkCallouts,
  PlanCardListItemFiberConnection,
  PlanCardBroadbandLinkoutItem,
  PlanCardOfferInfo,
  useOfferContext,
  useProviderContext,
  getPricingInfo,
} from '@updater/ui-widgets';
import { WifiHigh } from '@phosphor-icons/react';
import { TvInternetOffer } from '@updater/consumer-graph';
import { PlanCardListItemEquipmentRental } from 'components/PlanCard/PlanCardListItemEquipmentRental';
import { getProviderConfig } from 'config';
import { PriceOverTime } from './plan-details/PriceOverTime';
import { InternetSpeedSection } from './plan-details/InternetSpeedSection';
import { PlanInformationSection } from './plan-details/PlanInformationSection';
import { PromotionsSection } from './plan-details/PromotionsSection';
import { GreatForSection } from './plan-details/GreatForSection';

export interface AccentPlanCardDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan?: () => void;
  /**
   * The 1-year / 5-year switch, rendered above the plan details. The caller
   * owns it so this modal and the card behind it read the same term state.
   */
  termToggle?: ReactNode;
}

const DrawerHeader: FC = () => {
  const { logo, name: providerName, code } = useProviderContext();
  const { offerTitle, products } = useOfferContext() as TvInternetOffer;
  const hasInternetOnly =
    Boolean(products?.internet) &&
    !products?.tv &&
    !products?.phone &&
    !products?.mobile;

  return (
    <Box
      mb="m"
      pt="m"
      pl="m"
      pr="xxl"
      borderBottom={`1px solid ${Palette.gray300}`}
    >
      <Flex alignItems="center" pb="s">
        {logo?.url && (
          <Box flexShrink="0" mr="s">
            <Image
              src={logo.url}
              alt={`logo-${code || providerName}`}
              height="auto"
              maxHeight={48}
              maxWidth={48}
              objectFit="contain"
            />
          </Box>
        )}
        <Box>
          <Text variant="lBold">{offerTitle}</Text>
          {hasInternetOnly && (
            <Flex alignItems="center">
              <WifiHigh size={16} />
              <Text variant="xs" ml="xxs">
                Internet only
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

const DrawerFooter: FC<{ onSelectPlan?: () => void }> = ({ onSelectPlan }) => {
  const { pricing } = useOfferContext() as TvInternetOffer;
  const { code: providerCode } = useProviderContext();
  const price = pricing?.price;
  const showTaxesAndFees = getProviderConfig({
    providerCode,
  })?.showTaxesAndFees;

  const pricingInfo = pricing ? getPricingInfo(pricing) : null;
  const promoSubtext =
    pricingInfo?.crossedPrice &&
    pricingInfo.monthsApplied &&
    pricingInfo.monthsApplied !== 999
      ? `First ${pricingInfo.monthsApplied} months, then $${Math.round(
          pricingInfo.crossedPrice
        )}/mo`
      : undefined;

  return (
    <Flex
      px="m"
      py="s"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="white"
      borderTop={`1px solid ${Palette.gray300}`}
      flexShrink="0"
    >
      <Box>
        <Text variant="xs">Total monthly price</Text>
        <Text variant="lBold" color={Palette.gray900}>
          ${price != null ? Math.round(price) : '—'}
          <Text as="span" variant="xs" color={Palette.gray900}>
            /month
          </Text>
        </Text>
        {showTaxesAndFees && (
          <Text
            variant="xxs"
            color={Palette.gray700}
            data-testid="taxes-and-fees"
          >
            + taxes & fees
          </Text>
        )}
        {promoSubtext && (
          <Text variant="xxs" color={Palette.gray700}>
            {promoSubtext}
          </Text>
        )}
      </Box>
      {onSelectPlan && <Button onClick={onSelectPlan}>Select this plan</Button>}
    </Flex>
  );
};

export const AccentPlanCardDetailsModal: FC<
  AccentPlanCardDetailsModalProps
> = ({ isOpen, onClose, onSelectPlan, termToggle }) => {
  const setIsOpen = (open: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof open === 'function' ? open(isOpen) : open;
    if (!resolved) onClose();
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <ModalOverlay />
      <ModalContent
        height={['92%', '92%', 'auto']}
        maxHeight={['92%', '92%', '85%']}
        width="100%"
        maxWidth={['100%', '100%', '700px']}
        borderRadius="m"
        overflowY="hidden"
        marginTop="auto"
        marginBottom={['none', 'none', 'auto']}
      >
        <Flex flexDirection="column" height="100%">
          <ModalCloseButton size={24} />
          <Box overflowY="auto" flex={1}>
            <DrawerHeader />
            <Box px="m" pb="m">
              {termToggle && <Box mb="m">{termToggle}</Box>}
              <PriceOverTime />
              <InternetSpeedSection />
              <PlanInformationSection />
              <Text variant="mBold" mb="s">
                Plan highlights
              </Text>
              <PlanCardList>
                <PlanCardListItemInstantOn />
                <PlanCardListItemTelevision />
                <PlanCardListItemBuildings />
                <PlanCardListItemDiscount />
                <PlanCardDummyOfferDisclaimer />
                <PlanCardListItemContractLength />
                <PlanCardListItemAttOffer />
                <PlanCardListItemStarryOffer />
                <PlanCardListItemStarryEquipmentDetail />
                <PlanCardListItemStarlinkCallouts />
                <PlanCardListItemEquipmentRental />
                <PlanCardListItemFiberConnection />
                <PlanCardListItemAutoPayDisclaimer />
                <PlanCardBroadbandLinkoutItem />
              </PlanCardList>
              <PlanCardOfferInfo />
              <PromotionsSection />
              <GreatForSection />
            </Box>
          </Box>
          <DrawerFooter onSelectPlan={onSelectPlan} />
        </Flex>
      </ModalContent>
    </Modal>
  );
};
